/**
 * Nav Menu Builder
 * Reads nav-family, nav-shop-category, and nav-learn-item section JSON data and builds desktop/mobile navigation.
 *
 * This script:
 * 1. Reads all [data-nav-family] JSON elements (family members with products)
 * 2. Reads all [data-nav-shop-category] JSON elements (simple product categories)
 * 3. Reads all [data-nav-learn-item] JSON elements (dedicated learn items)
 * 4. Separates family members by type (shop vs learn)
 * 5. Builds desktop dropdown HTML for Shop and Learn menus
 * 6. Builds mobile nav HTML for categories, family list, and learn list
 * 7. Injects content into respective containers
 */

(function() {
  'use strict';

  /**
   * Read all nav family JSON data from the page
   * @returns {Array} Array of family member objects
   */
  function readNavFamilies() {
    const familyElements = document.querySelectorAll('[data-nav-family]');
    const families = [];

    familyElements.forEach(element => {
      try {
        const data = JSON.parse(element.textContent);
        families.push(data);
      } catch (e) {
        console.error('Failed to parse nav family JSON:', e);
      }
    });

    return families;
  }

  /**
   * Read all nav shop category JSON data from the page
   * @returns {Array} Array of category objects
   */
  function readNavShopCategories() {
    const categoryElements = document.querySelectorAll('[data-nav-shop-category]');
    const categories = [];

    categoryElements.forEach(element => {
      try {
        const data = JSON.parse(element.textContent);
        categories.push(data);
      } catch (e) {
        console.error('Failed to parse nav shop category JSON:', e);
      }
    });

    return categories;
  }

  /**
   * Read all nav learn item JSON data from the page
   * @returns {Array} Array of learn item objects
   */
  function readNavLearnItems() {
    const learnElements = document.querySelectorAll('[data-nav-learn-item]');
    const items = [];

    learnElements.forEach(element => {
      try {
        const data = JSON.parse(element.textContent);
        items.push(data);
      } catch (e) {
        console.error('Failed to parse nav learn item JSON:', e);
      }
    });

    return items;
  }

  /**
   * Separate families by menu type
   * @param {Array} families - All families
   * @returns {Object} Object with shop and learn arrays
   */
  function categorizeByType(families) {
    return {
      shop: families.filter(fam => fam.type === 'shop'),
      learn: families.filter(fam => fam.type === 'learn')
    };
  }

  /**
   * Generate HTML for a product card (desktop)
   * @param {Object} product - Product item data
   * @returns {string} HTML string
   */
  function buildDesktopProductCard(product) {
    const imageHtml = product.image
      ? `<div class="desktop-nav-v3__product-image">
          <img src="${product.image}" alt="${product.title || ''}" loading="lazy">
        </div>`
      : `<div class="desktop-nav-v3__product-image">
          <div class="desktop-nav-v3__product-placeholder"></div>
        </div>`;

    const badgeHtml = product.showNewBadge
      ? `<span class="desktop-nav-v3__product-badge">NEW!</span>`
      : '';

    // The short-title A/B test won, so always render the short metafield title,
    // falling back to the full title when custom.short_name is empty.
    const title = product.shortTitle || product.title || '';
    const titleHtml = `<span class="desktop-nav-v3__product-title">${title}</span>`;

    return `
      <a href="${product.url || '#'}" class="desktop-nav-v3__product-card">
        ${imageHtml}
        <div class="desktop-nav-v3__product-content">
          <div class="desktop-nav-v3__product-title-row">
            ${badgeHtml}${titleHtml}
          </div>
          <span class="desktop-nav-v3__product-description">${product.description || ''}</span>
        </div>
      </a>
    `;
  }

  /**
   * Generate HTML for a family column (desktop)
   * @param {Object} family - Family data with items
   * @returns {string} HTML string
   */
  function buildDesktopFamilyColumn(family) {
    const subtitleHtml = family.subtitle
      ? `<span class="desktop-nav-v3__category-subtitle">${family.subtitle}</span>`
      : '';

    const productItems = family.items
      .filter(item => item.type === 'product')
      .map(buildDesktopProductCard)
      .join('');

    return `
      <div class="desktop-nav-v3__category">
        <div class="desktop-nav-v3__category-header">
          <span class="desktop-nav-v3__category-title">${family.title || ''}</span>
          ${subtitleHtml}
        </div>
        <div class="desktop-nav-v3__category-products">
          ${productItems}
        </div>
      </div>
    `;
  }

  /**
   * Build the desktop Shop dropdown HTML
   * @param {Array} shopFamilies - Array of shop family objects
   * @returns {string} HTML string for dropdown inner content
   */
  function buildDesktopShopDropdown(shopFamilies) {
    if (shopFamilies.length === 0) {
      return '<div class="desktop-nav-v3__empty">No shop categories configured.</div>';
    }

    const columnsHtml = shopFamilies.map(buildDesktopFamilyColumn).join('');

    return `
      <div class="desktop-nav-v3__columns">
        ${columnsHtml}
      </div>
    `;
  }

  /**
   * Generate HTML for a category item (mobile - simple, no subtitle)
   * @param {Object} category - Category data
   * @returns {string} HTML string
   */
  function buildMobileCategoryItem(category) {
    const imageHtml = category.image
      ? `<div class="mobile-nav-v3__category-image">
          <img src="${category.image}" alt="${category.title || ''}" loading="lazy">
        </div>`
      : `<div class="mobile-nav-v3__category-image">
          <div class="mobile-nav-v3__category-placeholder"></div>
        </div>`;

    return `
      <a href="${category.url || '#'}" class="mobile-nav-v3__category-item">
        ${imageHtml}
        <div class="mobile-nav-v3__category-content">
          <span class="mobile-nav-v3__category-title">${category.title || ''}</span>
        </div>
        <span class="mobile-nav-v3__arrow">
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M13.6313 17.2154L12.985 16.5452L17.0735 12.4567H5.1543V11.5337H17.0735L12.985 7.44517L13.6313 6.78442L18.8468 11.9999L13.6313 17.2154Z" fill="#2C3D50"/>
</svg>
        </span>
      </a>
    `;
  }

  /**
   * Generate HTML for a family item (mobile)
   * @param {Object} family - Family data
   * @returns {string} HTML string
   */
  function buildMobileFamilyItem(family) {
    const imageHtml = family.image
      ? `<div class="mobile-nav-v3__family-image">
          <img src="${family.image}" alt="${family.title || ''}" loading="lazy">
        </div>`
      : `<div class="mobile-nav-v3__family-image">
          <div class="mobile-nav-v3__family-placeholder"></div>
        </div>`;

    const subtitleHtml = family.subtitle
      ? `<span class="mobile-nav-v3__family-subtitle">${family.subtitle}</span>`
      : '';

    return `
      <a href="${family.url || '#'}" class="mobile-nav-v3__family-item">
        ${imageHtml}
        <div class="mobile-nav-v3__family-content">
          <span class="mobile-nav-v3__family-title">${family.title || ''}</span>
          ${subtitleHtml}
        </div>
        <span class="mobile-nav-v3__arrow">
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M13.6313 17.2154L12.985 16.5452L17.0735 12.4567H5.1543V11.5337H17.0735L12.985 7.44517L13.6313 6.78442L18.8468 11.9999L13.6313 17.2154Z" fill="#2C3D50"/>
</svg>
        </span>
      </a>
    `;
  }

  /**
   * Generate HTML for a learn item (mobile)
   * @param {Object} item - Learn item data
   * @returns {string} HTML string
   */
  function buildMobileLearnItem(item) {
    return `
      <a href="${item.url || '#'}" class="mobile-nav-v3__learn-item">
        <div class="mobile-nav-v3__learn-content">
          <span class="mobile-nav-v3__learn-title">${item.title || ''}</span>
        </div>
        <span class="mobile-nav-v3__arrow">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M13.6313 17.2154L12.985 16.5452L17.0735 12.4567H5.1543V11.5337H17.0735L12.985 7.44517L13.6313 6.78442L18.8468 11.9999L13.6313 17.2154Z" fill="#2C3D50"/>
          </svg>
        </span>
      </a>
    `;
  }

  /**
   * Build the mobile category list HTML
   * @param {Array} categories - Array of category objects
   * @returns {string} HTML string
   */
  function buildMobileCategoryList(categories) {
    if (categories.length === 0) {
      return '';
    }

    const itemsHtml = categories.map(buildMobileCategoryItem).join('');

    return `
      <div class="mobile-nav-v3__section">
        <h3 class="mobile-nav-v3__section-title">Shop By Category</h3>
        <div class="mobile-nav-v3__list">
          ${itemsHtml}
        </div>
      </div>
    `;
  }

  /**
   * Build the mobile family list HTML
   * @param {Array} shopFamilies - Array of shop family objects
   * @returns {string} HTML string
   */
  function buildMobileFamilyList(shopFamilies) {
    if (shopFamilies.length === 0) {
      return '';
    }

    const itemsHtml = shopFamilies.map(buildMobileFamilyItem).join('');

    return `
      <div class="mobile-nav-v3__section">
        <h3 class="mobile-nav-v3__section-title">Shop By Family Member</h3>
        <div class="mobile-nav-v3__list">
          ${itemsHtml}
        </div>
      </div>
    `;
  }

  /**
   * Build the mobile learn list HTML (as collapsible accordion)
   * @param {Array} learnItems - Array of learn item objects from Nav - Learn sections
   * @returns {string} HTML string
   */
  function buildMobileLearnList(learnItems) {
    if (learnItems.length === 0) {
      return '';
    }

    const itemsHtml = learnItems.map(buildMobileLearnItem).join('');

    return `
      <div class="mobile-nav-v3__section">
        <div class="mobile-nav-v3__accordion-header" data-learn-accordion-toggle>
          <span class="mobile-nav-v3__section-title">Learn</span>
          <span class="mobile-nav-v3__accordion-chevron">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" fill="#2C3D50"/>
            </svg>
          </span>
        </div>
        <div class="mobile-nav-v3__list mobile-nav-v3__accordion-content" data-learn-accordion-content style="display: none;">
          ${itemsHtml}
        </div>
      </div>
    `;
  }

  /**
   * Inject desktop navigation content
   * @param {Object} categorized - Categorized data with shop and learn arrays
   */
  function injectDesktopNav(categorized) {
    // Inject Shop dropdown content
    const shopDropdownContainer = document.querySelector('[data-nav-shop-dropdown]');
    if (shopDropdownContainer) {
      shopDropdownContainer.innerHTML = buildDesktopShopDropdown(categorized.shop);
    }

    // Inject Learn dropdown content (labels and URLs only, no icons)
    const learnDropdownContainer = document.querySelector('[data-nav-learn-dropdown]');
    if (learnDropdownContainer) {
      const learnItems = readNavLearnItems();
      const linksHtml = learnItems.map(item =>
        `<a href="${item.url || '#'}" class="desktop-nav-v3__learn-link">${item.title || ''}</a>`
      ).join('');
      learnDropdownContainer.innerHTML = linksHtml || '<span class="desktop-nav-v3__empty">No learn items configured.</span>';
    }
  }

  /**
   * Inject mobile navigation content
   * @param {Object} categorized - Categorized data with shop and learn arrays
   * @param {Array} shopCategories - Array of shop category objects
   * @param {Array} learnItems - Array of dedicated learn item objects
   */
  function injectMobileNav(categorized, shopCategories, learnItems) {
    // Inject into ALL matching containers (header-drawer is rendered twice)
    const shopContainers = document.querySelectorAll('[data-mobile-nav-shop]');
    shopContainers.forEach(container => {
      // Order: Categories first, then Family Members
      const categoriesHtml = buildMobileCategoryList(shopCategories);
      const familyHtml = buildMobileFamilyList(categorized.shop);
      container.innerHTML = categoriesHtml + familyHtml;
    });

    // Inject Learn List into ALL matching containers
    const learnListContainers = document.querySelectorAll('[data-mobile-nav-learn]');
    learnListContainers.forEach(container => {
      container.innerHTML = buildMobileLearnList(learnItems);
    });
  }

  /**
   * Initialize learn accordion toggle behavior
   */
  function initLearnAccordion() {
    document.querySelectorAll('[data-learn-accordion-toggle]').forEach(header => {
      header.addEventListener('click', () => {
        const content = header.nextElementSibling;
        const chevron = header.querySelector('.mobile-nav-v3__accordion-chevron');
        const isOpen = content.style.display !== 'none';

        content.style.display = isOpen ? 'none' : 'block';
        chevron.classList.toggle('mobile-nav-v3__accordion-chevron--open', !isOpen);
      });
    });
  }

  /**
   * Initialize the nav menu builder
   */
  function init() {
    const families = readNavFamilies();
    const shopCategories = readNavShopCategories();
    const learnItems = readNavLearnItems();

    if (families.length === 0 && shopCategories.length === 0 && learnItems.length === 0) {
      console.log('NavMenuBuilder: No nav families, categories, or learn items found on page.');
      return;
    }

    const categorized = categorizeByType(families);

    console.log('NavMenuBuilder: Found', shopCategories.length, 'categories,', categorized.shop.length, 'shop families, and', learnItems.length, 'learn items');

    // Build and inject navigation
    injectDesktopNav(categorized);
    injectMobileNav(categorized, shopCategories, learnItems);

    // Initialize accordion behavior for learn section
    initLearnAccordion();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also re-run when Shopify editor updates sections
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
  document.addEventListener('shopify:section:reorder', init);
})();
